import { createSelector } from '@reduxjs/toolkit';
import { max, mean, min } from 'lodash';
import { DefaultRootState } from 'react-redux';
import { filterOptionSelector } from '../../layout/selectors/layout.selectors';
import { FilterOption } from '../../layout/types/layout-state.type';
import { getPercentile } from '../../utils/math';
import { CLUSTER_CODES, emptyMessageContent } from '../client/helpers';
import { isNotNullCluster } from '../clustering.types';
import {
  selectAll,
  selectById,
  selectEntities,
} from '../reducers/cluster.reducer';
import {
  morphologiesSelector,
  morphologyByIdSelector,
} from './morphology.selectors';

const X_AXIS_LOW_THRESHOLD = 800;

const clusterSelector = ({ cluster }: DefaultRootState) => cluster;

export const clustersSelector = createSelector(clusterSelector, selectAll);

export const clusterEntities = createSelector(clusterSelector, selectEntities);

export const selectedClusterIdsSelector = createSelector(
  clusterSelector,
  ({ selectedIds }) => selectedIds,
);

export const projectedBaseClusterSelector = createSelector(
  clusterEntities,
  selectedClusterIdsSelector,
  (entities, selectedIds) => {
    if (selectedIds.size > 0) {
      const projectedId = Array.from(selectedIds)[0];
      return entities[projectedId];
    } else {
      return undefined;
    }
  },
);

export const selectedBaseClustersSelector = createSelector(
  clusterEntities,
  selectedClusterIdsSelector,
  (entities, selectedIds) =>
    Array.from(selectedIds)
      .map((selectedId) => entities[selectedId])
      .filter(isNotNullCluster),
);

export const selectedCusterBeatsSelector = createSelector(
  projectedBaseClusterSelector,
  (cluster) => cluster?.beats ?? [],
);

export const meanClusterOnsetOffsetDurationSelector = createSelector(
  selectedCusterBeatsSelector,
  (beats) => mean(beats.map((beat) => beat.offset - beat.onset)),
);

export const clusterByIdSelector = (clusterId: number) =>
  createSelector(clusterSelector, (state) => selectById(state, clusterId));

export const clusterVerticalRangeSelector = createSelector(
  clustersSelector,
  morphologiesSelector,
  (clusters, morphologies) => {
    const allBeats = [...clusters, ...morphologies].flatMap(
      (cluster) => cluster.beats,
    );
    // we calculate the min and max values of the beats of all the clusters
    const beatsMinMax = allBeats.reduce(
      (acc, beat) => {
        const minBeat = min(beat.data) ?? 0;
        const maxBeat = max(beat.data) ?? 0;
        acc[0].push(minBeat);
        acc[1].push(maxBeat);
        return acc;
      },
      [[], []] as number[][],
    );

    // we take the value of the 5th percentile of all the min values
    const minValue = getPercentile(beatsMinMax[0], 0.05);
    // we take the value of the 95th percentile of all the max values
    const maxValue = getPercentile(beatsMinMax[1], 0.95);

    return { yMin: minValue, yMax: maxValue };
  },
);

export const clusterHorizontalLengthSelector = (clusterId: number) =>
  createSelector(
    clusterByIdSelector(clusterId),
    morphologyByIdSelector(clusterId),
    (baseCluster, morphology) => {
      const cluster = baseCluster ?? morphology;
      return (
        max(cluster?.beats.flatMap((beat) => beat.data?.length)) ??
        X_AXIS_LOW_THRESHOLD
      );
    },
  );

export const clusterByFilterSelector = (filterOption: FilterOption) =>
  createSelector(clustersSelector, (clusters) =>
    filterOption === 'All'
      ? clusters
      : clusters.filter((cluster) => cluster.classification === filterOption),
  );

export const baseClustersSelector = createSelector(
  clustersSelector,
  filterOptionSelector,
  (clusters, filterOption) =>
    filterOption === 'All'
      ? clusters
      : clusters.filter((cluster) => cluster.classification === filterOption),
);

export const indexSelectedSelector = createSelector(
  baseClustersSelector,
  projectedBaseClusterSelector,
  (clusters, projectedCluster) => {
    if (projectedCluster == null) {
      return 0;
    }
    return clusters.findIndex((cluster) => cluster.id === projectedCluster.id);
  },
);

export const isLastClusterSelector = createSelector(
  baseClustersSelector,
  indexSelectedSelector,
  (clusters, indexClusterSelected) => {
    return !isNotNullCluster(clusters[indexClusterSelected + 1]);
  },
);

export const baseClusterIdsSelector = createSelector(
  baseClustersSelector,
  (clusters) => clusters.map((cluster) => cluster.id),
);

export const unclassifiedClustersSelector = createSelector(
  clustersSelector,
  (clusters) =>
    clusters.filter((cluster) => cluster.classification === 'Unclassified'),
);

export const showLoaderSelector = createSelector(
  clustersSelector,
  morphologiesSelector,
  (clusters, morphologies) =>
    clusters.length === 0 && morphologies.length === 0,
);

export const disableMergeSelector = createSelector(
  selectedBaseClustersSelector,
  (clusters) =>
    clusters.length === 0 ||
    clusters.some(
      (cluster) => cluster.classification !== clusters[0].classification,
    ) ||
    clusters.some(
      (cluster) =>
        cluster.classification === 'Noise' ||
        cluster.classification === 'Unclassified',
    ),
);

export const clustersWithoutNoiseSelector = createSelector(
  clustersSelector,
  (clusters) =>
    clusters.filter((cluster) => cluster.classification !== 'Noise'),
);

export const unmergedClustersSelector = createSelector(
  clustersWithoutNoiseSelector,
  selectedClusterIdsSelector,
  (clusters, selectedIds) =>
    clusters.filter((cluster) => !selectedIds.has(cluster.id)),
);

export const sizeByClassificationSelector = createSelector(
  clustersSelector,
  (clusters) => {
    const classifications = Object.values(CLUSTER_CODES);
    return classifications.reduce(
      (acc, curr) => ({
        ...acc,
        [curr]: clusters.filter((cluster) => cluster.classification === curr)
          .length,
      }),
      {
        All: clusters.length,
      },
    ) as Record<FilterOption, number>;
  },
);

export const showEmptyClusterStateSelector = createSelector(
  baseClustersSelector,
  clustersSelector,
  (filteredClusters, allClusters) =>
    filteredClusters.length === 0 && allClusters.length !== 0,
);

export const emptyMessageClusterSelector = createSelector(
  baseClustersSelector,
  (filteredClusters) => {
    if (filteredClusters.length === 0) {
      return emptyMessageContent['noFilteredClusters'];
    }
    return emptyMessageContent['default'];
  },
);
